import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, Cloud, MessageCircle, Cpu } from 'react-feather';
import { Alert, Badge, Button, Card, Col, Dropdown, Row } from 'react-bootstrap';
import { Container } from 'reactstrap';
import { toast } from 'react-toastify';
import { getIntegrationHubAPI, runProviderTestAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import { companyUrl } from '../../../utils/mhNav';
import styles from './IntegrationHub.module.scss';

const ICONS = { microsoft: Cloud, google: Cloud, whatsapp: MessageCircle, openai: Cpu };

const STATUS_VARIANT = {
  connected: 'success',
  not_connected: 'secondary',
  error: 'danger',
  pending: 'warning',
};

export default function IntegrationHub() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getIntegrationHubAPI();
      setData(res.data);
      setHasLoaded(true);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const errorProvider = useMemo(() => data?.providers?.find((p) => p.status === 'error'), [data]);

  const runTest = async (id) => {
    try {
      await runProviderTestAPI(id);
      toast.success('Connectivity test OK');
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const connectDropdown = (
    <Dropdown align="end">
      <Dropdown.Toggle variant="primary" id="dd-connect">
        Connect provider
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Item onClick={() => navigate(companyUrl('integrations/microsoft'))}>Microsoft</Dropdown.Item>
        <Dropdown.Item onClick={() => navigate(companyUrl('integrations/google'))}>Google</Dropdown.Item>
        <Dropdown.Item onClick={() => navigate(companyUrl('integrations/whatsapp'))}>WhatsApp</Dropdown.Item>
        <Dropdown.Item onClick={() => toast.info('OpenAI setup coming soon')}>OpenAI</Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );

  if (loading && !hasLoaded) {
    return (
      <Fragment>
        <PageHeader title="Integration hub" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Integrations' }]} />
        <Container fluid className="mh-page-body">
          <LoadingSkeleton rows={4} cols={2} />
        </Container>
      </Fragment>
    );
  }

  if (error && !hasLoaded) {
    return (
      <Fragment>
        <PageHeader title="Integration hub" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Integrations' }]} />
        <Container fluid className="mh-page-body">
          <EmptyState icon={AlertTriangle} title="Could not load" description={error.message} actionLabel="Retry" onAction={load} />
        </Container>
      </Fragment>
    );
  }

  const providers = data?.providers || [];
  const checklists = data?.checklists || {};

  if (!loading && !providers.length) {
    return (
      <Fragment>
        <PageHeader title="Integration hub" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Integrations' }]} />
        <Container fluid className="mh-page-body">
          <EmptyState icon={AlertTriangle} title="No providers" description="Nothing configured." actionLabel="Reload" onAction={load} />
        </Container>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <PageHeader
        title="Integration hub"
        breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Integrations' }]}
        extra={connectDropdown}
      />

      <Container fluid className="mh-page-body">
        {errorProvider && (
          <Alert variant="warning" className={`${styles.bannerWarn} d-flex flex-wrap align-items-center gap-3 mb-4`}>
            <span>
              <strong>{errorProvider.name}</strong> connection requires attention
            </span>
            <Button variant="warning" onClick={() => navigate(companyUrl(errorProvider.setupPath))}>
              Fix now
            </Button>
          </Alert>
        )}

        <Row className="g-4 mb-4">
          {providers.map((p) => {
            const Icon = ICONS[p.id] || Cloud;
            const label = p.status.replace(/_/g, ' ');
            return (
              <Col md={6} key={p.id}>
                <Card className={styles.cardProvider}>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                      <div className="d-flex align-items-center gap-3 min-w-0">
                        <div className={styles.cardIcon}>
                          <Icon size={22} />
                        </div>
                        <Card.Title className="h5 mb-0">{p.name}</Card.Title>
                      </div>
                      <Badge bg={STATUS_VARIANT[p.status] || 'secondary'} className={`text-capitalize ${styles.statusBadge}`}>
                        {label}
                      </Badge>
                    </div>
                    <p className="small text-muted mb-2">
                      Last success: {p.lastSuccessAt ? new Date(p.lastSuccessAt).toLocaleString() : '—'}
                    </p>
                    <p className="small text-muted mb-4">Owner: {p.ownerName}</p>
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                      {p.status === 'not_connected' || p.status === 'pending' ? (
                        <Button onClick={() => navigate(companyUrl(p.setupPath))}>Connect</Button>
                      ) : (
                        <>
                          <Button variant="primary" onClick={() => navigate(companyUrl(p.setupPath))}>
                            {p.status === 'error' ? 'Reconnect' : 'Manage'}
                          </Button>
                          <Button variant="light" onClick={() => runTest(p.id)}>
                            Test
                          </Button>
                        </>
                      )}
                      <Button variant="link" className="ms-1" as={Link} to={companyUrl('audit')}>
                        {p.logsLabel}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>

        <h2 className="h6 mt-2 mb-3">Setup checklists</h2>
        {providers.map((p) => {
          const steps = checklists[p.id];
          if (!steps?.length) return null;
          return (
            <Card key={`cl-${p.id}`} className="mb-4">
              <Card.Header as="h3" className="h6 mb-0">
                {p.name}
              </Card.Header>
              <Card.Body>
                {steps.map((s) => (
                  <div key={s.id} className={styles.checkRow}>
                    <span className={styles.checkGlyph} aria-hidden>
                      {s.done ? '✓' : s.warning ? '⚠' : '✗'}
                    </span>
                    <span>{s.label}</span>
                  </div>
                ))}
              </Card.Body>
            </Card>
          );
        })}
      </Container>
    </Fragment>
  );
}
